import {ChartDataset, ChartOptions} from 'chart.js';
import {
  PlayerComparisonV2Benchmark,
  PlayerComparisonV2Item,
} from '@/features/players/api';
import {Theme} from '@/theme/theme';

export const comparisonSkills = ['Shooting', 'Passing', 'Pace', 'Dribbling', 'Defending', 'Physical'] as const;

type SkillSource = Pick<PlayerComparisonV2Item, 'scouting'> | Pick<PlayerComparisonV2Benchmark, 'scouting'>;

export const comparisonSkillValues = (source: SkillSource): number[] => [
  source.scouting.shooting,
  source.scouting.passing,
  source.scouting.pace,
  source.scouting.dribbling,
  source.scouting.defending,
  source.scouting.physical,
];

const lightColors = ['#1d4ed8', '#a21caf', '#047857', '#c2410c'];
const darkColors = ['#60a5fa', '#e879f9', '#34d399', '#fb923c'];
const benchmarkLightColors = ['#111827', '#4b5563', '#7c2d12', '#164e63'];
const benchmarkDarkColors = ['#f9fafb', '#d1d5db', '#fed7aa', '#a5f3fc'];

export const buildComparisonDatasets = (
  players: PlayerComparisonV2Item[],
  benchmarks: PlayerComparisonV2Benchmark[],
  theme: Theme,
): ChartDataset<'radar', number[]>[] => {
  const playerColors = theme === 'dark' ? darkColors : lightColors;
  const benchmarkColors = theme === 'dark' ? benchmarkDarkColors : benchmarkLightColors;
  return [
    ...players.map((player, index) => ({
      label: player.name,
      data: comparisonSkillValues(player),
      borderColor: playerColors[index % playerColors.length],
      backgroundColor: theme === 'dark' ? 'rgba(96, 165, 250, 0.12)' : 'rgba(29, 78, 216, 0.10)',
      pointBackgroundColor: playerColors[index % playerColors.length],
      pointBorderColor: theme === 'dark' ? '#111827' : '#ffffff',
      pointStyle: 'circle' as const,
      borderWidth: 2,
    })),
    ...benchmarks.map((benchmark, index) => ({
      label: `${benchmark.position} ${benchmark.scope} benchmark`,
      data: comparisonSkillValues(benchmark),
      borderColor: benchmarkColors[index % benchmarkColors.length],
      backgroundColor: 'rgba(0, 0, 0, 0)',
      pointBackgroundColor: benchmarkColors[index % benchmarkColors.length],
      pointBorderColor: theme === 'dark' ? '#111827' : '#ffffff',
      pointStyle: 'rectRot' as const,
      borderDash: [8, 4],
      borderWidth: 2,
    })),
  ];
};

export const buildComparisonChartOptions = (theme: Theme): ChartOptions<'radar'> => {
  const text = theme === 'dark' ? '#e5e7eb' : '#374151';
  const grid = theme === 'dark' ? 'rgba(156, 163, 175, 0.35)' : 'rgba(107, 114, 128, 0.28)';
  return {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {labels: {color: text, usePointStyle: true, padding: 16}},
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {color: text, backdropColor: 'transparent', stepSize: 20},
        pointLabels: {color: text, font: {size: 13}},
        grid: {color: grid},
        angleLines: {color: grid},
      },
    },
  };
};
